import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Select,
  Button,
  message,
  Spin,
  Tag,
  Space,
  Alert,
} from "antd";
import {
  SendOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { getPartners } from "../../partners/services/partnerService.js";
import * as orderService from "../services/orderService.js";
import TruncatedText from "../../../shared/components/TruncatedText.jsx";
import dayjs from "dayjs";

const { Option } = Select;

/**
 * Partner Job Pack Modal
 * Allows admin to select pending/in-progress orders and send them to a partner as PDF
 */
const PartnerJobPackModal = ({ visible, onClose, orders = [] }) => {
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  // Filter orders to only pending and in_progress
  const eligibleOrders = orders.filter(
    (order) => order.status === "pending" || order.status === "in_progress"
  );

  // Load partners on mount
  useEffect(() => {
    if (visible) {
      loadPartners();
    }
  }, [visible]);

  const loadPartners = async () => {
    setLoading(true);
    try {
      const result = await getPartners({ isActive: true });
      if (result.success && result.data) {
        setPartners(result.data);
      } else {
        message.error("Failed to load partners");
      }
    } catch (error) {
      message.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedPartnerId) {
      message.warning("Please select a partner");
      return;
    }

    if (selectedOrderIds.length === 0) {
      message.warning("Please select at least one order");
      return;
    }

    setSending(true);
    try {
      const result = await orderService.sendPartnerJobPack({
        partnerId: selectedPartnerId,
        orderIds: selectedOrderIds,
      });

      if (result.success) {
        message.success(
          `Job pack sent successfully! Email queued (Job ID: ${result.jobId})`
        );
        // Reset selections
        setSelectedPartnerId(null);
        setSelectedOrderIds([]);
        onClose();
      } else {
        message.error(result.error || "Failed to send job pack");
      }
    } catch (error) {
      message.error(error.message || "Failed to send job pack");
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async () => {
    if (selectedOrderIds.length === 0) {
      message.warning("Please select at least one order");
      return;
    }

    setPreviewing(true);
    try {
      const result = await orderService.previewPartnerJobPack({
        orderIds: selectedOrderIds,
      });

      if (result.success && result.pdfUrl) {
        // Convert base64 to blob and create blob URL
        const base64Data = result.pdfUrl.split(',')[1]; // Remove data:application/pdf;base64, prefix
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        
        // Open PDF in new tab
        const newWindow = window.open(blobUrl, '_blank');
        if (newWindow) {
          // Clean up blob URL after a delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } else {
          message.error("Please allow pop-ups to preview the PDF");
        }
      } else {
        message.error(result.error || "Failed to generate preview");
      }
    } catch (error) {
      message.error(error.message || "Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (text) => <TruncatedText text={text} bold />,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 150,
      render: (text) => <TruncatedText text={text} maxLength={20} />,
    },
    {
      title: "Route",
      key: "route",
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: "13px" }}>
            <TruncatedText
              text={record.pickup?.address || record.pickup}
              maxLength={25}
            />
          </div>
          <div style={{ fontSize: "12px", color: "#999", margin: "2px 0" }}>
            →
          </div>
          <div style={{ fontSize: "13px" }}>
            <TruncatedText
              text={record.delivery?.address || record.delivery}
              maxLength={25}
            />
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const colorMap = {
          pending: "orange",
          in_progress: "blue",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {status?.replace("_", " ").toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority) => {
        const displayPriority = priority === "high" ? "URGENT" : "NORMAL";
        return <span>{displayPriority}</span>;
      },
    },
    {
      title: "Created",
      key: "created",
      width: 120,
      render: (_, record) => {
        if (!record.createdAt) return "N/A";
        try {
          return dayjs(record.createdAt).format("MMM DD, YYYY");
        } catch {
          return "N/A";
        }
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedOrderIds,
    onChange: (selectedRowKeys) => {
      setSelectedOrderIds(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: false,
      name: record.id,
    }),
  };

  const selectedPartner = partners.find((p) => p.id === selectedPartnerId);

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Send Job Pack to Partner</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="preview"
          icon={<FileTextOutlined />}
          onClick={handlePreview}
          loading={previewing}
          disabled={selectedOrderIds.length === 0}
        >
          Preview PDF
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          disabled={
            !selectedPartnerId || selectedOrderIds.length === 0 || sending
          }
        >
          Send to Partner
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message={`Found ${eligibleOrders.length} eligible orders (pending or in progress)`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
          Select Partner *
        </label>
        <Select
          style={{ width: "100%" }}
          placeholder="Choose a partner to send the job pack to"
          value={selectedPartnerId}
          onChange={setSelectedPartnerId}
          loading={loading}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {partners.map((partner) => (
            <Option key={partner.id} value={partner.id} label={partner.name}>
              <div>
                <div style={{ fontWeight: 600 }}>{partner.name}</div>
                {partner.email && (
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {partner.email}
                  </div>
                )}
                {partner.contactPerson && (
                  <div style={{ fontSize: "12px", color: "#999" }}>
                    Contact: {partner.contactPerson}
                  </div>
                )}
              </div>
            </Option>
          ))}
        </Select>
        {selectedPartner && !selectedPartner.email && (
          <Alert
            message="Warning: This partner does not have an email address configured"
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
          />
        )}
      </div>

      <div style={{ marginBottom: 8 }}>
        <Space>
          <span style={{ fontWeight: 600 }}>Select Orders:</span>
          {selectedOrderIds.length > 0 && (
            <Tag color="blue">
              {selectedOrderIds.length} selected
            </Tag>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={eligibleOrders}
        rowKey="id"
        rowSelection={rowSelection}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} orders`,
        }}
        scroll={{ y: 400 }}
        size="middle"
      />
    </Modal>
  );
};

export default PartnerJobPackModal;
